import { FC, useCallback, useMemo, useState } from "react";

import { getFieldsProps } from "../../../../ui/layout/organisms/SlideOver/SlideOverFixedLayouts/SlideOverFieldsGroups/helpers/getFieldsProps";
import { RenderFieldProps } from "../../../../ui/layout/organisms/SlideOver/SlideOverFixedLayouts/SlideOverFieldsGroups/types";
import { FieldsGroupWrapper } from "../../../../ui/layout/organisms/SlideOver/SlideOverFixedLayouts/SlideOverFieldsGroups/FieldsGroupWrapper";
import { UpdateCampaignBody } from "../../types";

const queryParamKeys = ["query", "url"];

type CampaignQueryItem = { [key in (typeof queryParamKeys)[number]]: string };

const EMPTY_QUERY: CampaignQueryItem = { query: "", url: "" };

const queryRenderProps: RenderFieldProps<keyof CampaignQueryItem>[] =
  queryParamKeys.map((key) => ({ key }));

export const QueryRow: FC<
  UpdateCampaignBody & {
    queries: CampaignQueryItem[];
  }
> = ({ queries, updateCampaignBody }) => {
  const [currentQueries, setCurrentQueries] =
    useState<CampaignQueryItem[]>(queries);

  const initConfig = useCallback(() => {
    setCurrentQueries(queries);
  }, [queries]);

  const {
    handleRemove,
    handleAdd,
    areSameFields,
    filterOutEmptyFields,
    fieldsRenderData,
  } = useMemo(
    () =>
      getFieldsProps<CampaignQueryItem>(
        setCurrentQueries,
        EMPTY_QUERY,
        queryParamKeys,
      ),
    [],
  );

  const isSameQueryConfig = useMemo(
    () => areSameFields(currentQueries, queries),
    [currentQueries, queries, areSameFields],
  );

  const submitFieldHandler = useCallback(
    async () =>
      await updateCampaignBody({
        autoSuggestCampaign: {
          queryMapping: filterOutEmptyFields(currentQueries).map(
            ({ url, query }) => ({
              query,
              target: { url, type: "url" },
            }),
          ),
        },
      }),
    [currentQueries, updateCampaignBody, filterOutEmptyFields],
  );

  const renderQueries = useMemo(
    () => fieldsRenderData(currentQueries, queryRenderProps),
    [currentQueries, fieldsRenderData],
  );

  return (
    <FieldsGroupWrapper
      fieldsType="query"
      initFieldsCount={queries.length}
      initConfig={initConfig}
      addFieldHandler={handleAdd}
      removeFieldHandler={handleRemove}
      allowSubmit={!isSameQueryConfig}
      submitFieldHandler={submitFieldHandler}
      fieldRender={renderQueries}
      importantFields={queryParamKeys}
    />
  );
};
